<template>
  <div class="model-widget">
    <div class="controls" >
      <multi-select-component
        v-show="varOptions.length > 0"
        label="variable"
        mode="single"
        v-model="currentVar"
        :options="varOptions"
        :configurationOptions="{'allow-empty': false, 'label-inside':true}"
      ></multi-select-component>
    </div>
    <div class="three-container" ref="container"></div>
    <div class="legend" ref="legendContainer"></div>
    <loading-overlay v-if="!ready">
      {{loadingMessage}}
      <template v-slot:disclaimer>3D model might take some time to load</template>
    </loading-overlay>
  </div>
</template>

<script>
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { throttle } from 'throttle-debounce'
import { debounce } from 'debounce'

import d3 from '@/utils/d3importer'
import widgetContent from '@/mixins/widgetContent'
import legend from '@/utils/ColorLegend'
import { getColorScale } from '@/utils/colorScales'
import MultiSelectComponent from '@/components/MultiSelectComponent'

// import {intersect} from '@/utils/3DIntersect'

import Api from '@/api'
export default {
  mixins: [widgetContent],
  components: { MultiSelectComponent },
  mounted() {
    // this.currentVariables = []
    this.init()
  },
  props: {
    lazyRender: {
      default: false,
    },
    currentTime: {
      default: 0,
    },
    currentSlice: {
      default: null,
    },
    currentSegment: {
      default: null,
    },
    textureColor: {
      default: '#ddd',
    },
    selectionColor: {
      default: '#fff',
    },
    selectionOpacity: {
      default: 0.4,
    },
    segmentVariables: {
      default: null,
    },
    showLegend: {
      default: true,
    },
    colorScale: {
      default: 'warm',
    },
    config: {
      default: () => {
        return {}
      },
    },
  },
  data() {
    return {
      currentSegmentVariables: this.segmentVariables,
      currentVar: 'Ca',
      loadingMessage: '',
    }
  },
  computed: {
    ready() {
      return this.config.variablesSrc ? this.variables !== null : this.model !== null
    },
    _colorScale() {
      let prop = this.colorScale
      if (prop.domain) return prop
      else {
        return getColorScale(prop)
      }
    },
    varOptions() {
      return this.dataBundle
        ? this.dataBundle.varKeys.map(e => {
            return {
              value: e,
              label: e,
              id: 3,
            }
          })
        : []
    },
    model() {
      return this.dataBundle ? this.dataBundle.model : null
    },
    segmentDict() {
      return this.dataBundle ? this.dataBundle.segmentDict : null
    },
    variables() {
      if (this.currentSegmentVariables)
        return this.convertSegmentVariables(this.currentSegmentVariables, this.segmentDict)
      else return this.dataBundle ? this.dataBundle.variables : null
    },
  },
  asyncComputed: {
    dataBundle() {
      return Api.get3dBundle(this.$store.state.paramsData, this.updateLoadingStatus)
    },
    // model () {
    //   return Api.get3dModel_NEW()
    // },
    // segmentDict () {
    //   return Api.get3DSegments_NEW() //ahapoints
    // },
    // variables () {
    //   if(this.config.hdfSrc) return Api.get3DVariablesHDF5(this.config.hdfSrc)
    //   // else if(this.config.variablesSrc) return Api.get3DVariables_NEW(this.config.variablesSrc)
    //   else if (this.currentSegmentVariables) return this.convertSegmentVariables(this.currentSegmentVariables, this.segmentDict)
    //   else return null
    // },
  },
  methods: {
    updateLoadingStatus(status) {
      this.loadingMessage = status
    },
    convertSegmentVariables(segVars) {
      let flatSegVars = segVars
        .map((data, i) => {
          let subdata = data.data || data
          return subdata.flat()
        })
        .flat()
      if (!this.segmentDict) return null
      var segmentsIds = this.segmentDict.AHA
      let vars = [
        {
          t: 0,
          values: segmentsIds.map((segId, i) => {
            let cand = flatSegVars.find(d => String(d.id) === String(segId))
            let res = {}
            res[this.currentVar] = cand ? +cand.value : null
            return res
          }),
        },
      ]
      return vars
    },
    computeCurrentVariables() {
      // TODO: Supone total time === 1, pero tiene sentido qeu el "currentValue" sea una prop normalizada, no?
      if (!this.variables) return null
      let variables = this.variables

      let idx, next, dt, t
      if (variables.length > 1) {
        idx = variables.findIndex(d => d.t > this.currentTime) - 1
        next = Math.min(idx + 1, variables.length - 1)
        dt = variables[next].t - variables[idx].t
        t = this.currentTime - dt * Math.floor(this.currentTime / dt)
      } else {
        idx = 0
        next = 0
        dt = 1
        t = 0
      }

      let numericValues = varValue => {
        return Object.keys(varValue).reduce((res, key) => {
          res[key] = isNaN(varValue[key]) ? this._colorScale.domain()[1] : varValue[key]
          return res
        }, {})
      }
      return variables[0].values.map((p, i) => {
        let int = d3.interpolate(
          numericValues(variables[idx].values[i]),
          numericValues(variables[next].values[i])
        )
        // if(i===0) console.log(idx,this.currentTime,t,t/dt,intraInt(t/dt))
        let interpolated = int(t / dt)
        return interpolated
      })
    },
    init() {
      let container = this.$refs.container
      let scene = new THREE.Scene()
      scene.background = new THREE.Color(0xffffff)
      let camera = new THREE.PerspectiveCamera(60, this.size.width / this.size.height, 1, 100)
      camera.position.set(0, 0, 14)
      scene.add(camera)

      var pointLight = new THREE.PointLight(0xffffff, 1)
      camera.add(pointLight)

      let renderer = new THREE.WebGLRenderer({ antialias: true })
      renderer.autoClear = false
      renderer.setPixelRatio(window.devicePixelRatio)
      renderer.setSize(this.size.width, this.size.height)
      container.appendChild(renderer.domElement)

      var controls = new OrbitControls(camera, renderer.domElement)
      controls.addEventListener('change', this.render)
      controls.addEventListener('start', () => (this.dragging = true))
      controls.addEventListener('end', () => (this.dragging = false))

      this._controls = controls
      this._renderer = renderer
      this._scene = scene
      this._camera = camera

      this._raycaster = new THREE.Raycaster()
      this._mouse = new THREE.Vector2()
      this.$refs.container.addEventListener('mousemove', this.onMouseMove, false)
    },
    loadModel() {
      // this.model.geometry.center().rotateX(Math.PI/2)
      var count = this.model.geometry.attributes.position.count
      this._originalGeometry = this.model.geometry
      this._geometry = this.model.geometry.clone()
      this._geometry.setAttribute(
        'color',
        new THREE.BufferAttribute(new Float32Array(count * 3), 3)
      )
      this._geometry.setAttribute(
        'selected',
        new THREE.BufferAttribute(new Float32Array(count * 1), 1)
      )

      this._geometry.computeBoundingSphere()

      var material = new THREE.MeshPhongMaterial({
        flatShading: true,
        vertexColors: true,
        shininess: 0,
        side: THREE.FrontSide,
      })
      let selCol = d3.color(this.selectionColor)
      var shaderMaterial = new THREE.ShaderMaterial({
        vertexShader: `
            attribute float selected;
            varying vec3 vColor;
            varying float vAlpha;
            void main() {
              vColor = vec3(${selCol.r / 255},${selCol.g / 255},${selCol.b / 255});
              vAlpha = selected <= 0.0 ? 0.0 : ${this.selectionOpacity};
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
            }
        `,
        fragmentShader: `
          varying vec3 vColor;
          varying float vAlpha;
          void main() {
            vec3 c = vColor;
            gl_FragColor = vec4(c.r,c.g,c.b,vAlpha);
          }
        `,
        vertexColors: THREE.VertexColors,
        wireframe: true,

        blending: THREE.CustomBlending,
        blendSrc: THREE.SrcAlphaFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor,
        blendEquation: THREE.AddEquation,

        side: THREE.DoubleSide,
      })

      this._geometry.clearGroups()
      // this._geometry.addGroup( 0, Infinity, 0 );
      // this._geometry.addGroup( 0, Infinity, 1 );
      this._geometry.addGroup(0, 250000, 0)
      this._geometry.addGroup(0, 250000, 1)
      let mesh = new THREE.Mesh(this._geometry, [shaderMaterial, material])

      //let mesh = new THREE.Mesh( this._geometry, material)

      let group = new THREE.Group()
      group.add(mesh)
      this._geometry.computeBoundingSphere()
      let c = this._geometry.boundingSphere.center
      group.rotateX(Math.PI / 2).rotateZ(-1)
      group.translateX(-c.x)
      group.translateY(-c.y)
      group.translateZ(-c.z)

      this._scene.add(group)
      this._mainGroup = group
      this._mesh = mesh

      this._scene.add(new THREE.AxesHelper(10))
    },

    loadVariables() {
      //un poco guarro este if... quizas deberia ser una prop: domain o algo, pero que palo...
      let domain
      if (this.config.variablesSrc) {
        domain = d3.extent(
          this.variables.map(v => d3.extent(v.values, d => d[this.currentVar])).flat()
        )
        this._colorScale.domain(domain)
      }
      this.loadSlice()
    },
    loadSlice() {
      var geometry = new THREE.PlaneGeometry(20, 20)
      // var material = new THREE.MeshBasicMaterial( {color: 0xff00ff, side: THREE.DoubleSide} );
      var material = new THREE.MeshPhongMaterial({
        color: 0xf0f0f0,
        opacity: 0.6,
        transparent: true,
        side: THREE.DoubleSide,
        flatShading: true,
        vertexColors: true,
        shininess: 0,
      })
      var plane = new THREE.Mesh(geometry, material)
      this._slicePlane = plane

      // var pointsMaterial = new THREE.PointsMaterial({
      //   size: .2,
      //   color: 0xffffff
      // });
      // let points = new THREE.Points(new THREE.Geometry(), pointsMaterial)
      // // points.renderOrder = 999;
      // // points.onBeforeRender = function( renderer ) { renderer.clearDepth(); };
      // this._slicePoints = points

      if (this.currentSlice) {
        // this._scene.add( points );
        this._scene.add(plane)
      }
    },

    updateColors() {
      if (!this.variables || !this._geometry) return null
      // console.log("segments dict",this.segmentDict)
      var colors = this._geometry.attributes.color
      var selecteds = this._geometry.attributes.selected

      var count = this._geometry.attributes.color.count
      let getPixelColor = i => {
        if (
          this.currentVariables &&
          this.currentVariables[i] &&
          !isNaN(parseFloat(this.currentVariables[i][this.currentVar]))
        ) {
          return d3.color(this._colorScale(this.currentVariables[i][this.currentVar]))
        } else {
          return d3.color(this.textureColor)
        }
      }
      if (this.currentSegment && this.segmentDict) {
        let currentSemgents = Array.isArray(this.currentSegment)
          ? this.currentSegment
          : [this.currentSegment]

        var segments = this.segmentDict.AHA
        let segmentColors = d3.scaleOrdinal(d3.schemePaired)
        for (var i = 0; i < count; i++) {
          let d3color = getPixelColor(i) //d3.color(this._colorScale(this.currentVariables[i]['INTRA']))
          colors.setXYZ(i, d3color.r / 255, d3color.g / 255, d3color.b / 255)

          if (currentSemgents.find(cs => cs.id === segments[i])) selecteds.setX(i, 1)
          else selecteds.setX(i, -1)
        }
      } else {
        for (var i = 0; i < count; i++) {
          let d3color = getPixelColor(i) //d3.color(this._colorScale(this.currentVariables[i]['INTRA']))
          colors.setXYZ(i, d3color.r / 255, d3color.g / 255, d3color.b / 255)
          selecteds.setX(i, 0)
        }
      }

      colors.needsUpdate = true
      selecteds.needsUpdate = true
    },
    updateDisplacements() {
      if (!this.currentVariables || !this.currentVariables[0].hasOwnProperty('DISPL_0')) return
      var positions = this._geometry.attributes.position
      var originalPositions = this._originalGeometry.attributes.position
      var count = this._geometry.attributes.position.count
      for (var i = 0; i < count; i++) {
        let current = {
          x: originalPositions.getX(i),
          y: originalPositions.getY(i),
          z: originalPositions.getZ(i),
        }
        // let displ = {x:+currentVariables[i]['DISPL_0']*this.currentTime,y:+currentVariables[i]['DISPL_1']*this.currentTime,z:+currentVariables[i]['DISPL_2']*this.currentTime}
        let displ = {
          x: this.currentVariables[i]['DISPL_0'],
          y: this.currentVariables[i]['DISPL_1'],
          z: this.currentVariables[i]['DISPL_2'],
        }
        positions.setXYZ(i, current.x + displ.x, current.y + displ.y, current.z + displ.z)
      }

      positions.needsUpdate = true
    },
    updateSlice() {
      // console.log("updating slice")
      if (this.currentSlice) {
        this._mainGroup.add(this._slicePlane)
        this._slicePlane.quaternion.setFromUnitVectors(
          new THREE.Vector3(0, 0, 1),
          this.currentSlice.normal.clone().normalize()
        )
        // this._slicePlane.up.set(this.currentSlice.normal.x,this.currentSlice.normal.y,this.currentSlice.normal.z)
        this._slicePlane.position.set(
          this.currentSlice.origin.x,
          this.currentSlice.origin.y,
          this.currentSlice.origin.z
        )
      } else {
        this._mainGroup.remove(this._slicePlane)
      }
      // Habria que intersect, pero no pinta muy bien...
      // let {points,contours} = intersect(new THREE.Mesh(new THREE.Geometry().fromBufferGeometry(this._geometry.toNonIndexed())),this.currentSlice)

      // // var pointsMesh = new THREE.Points(points, pointsMaterial);
      // this._slicePoints.geometry = points
      // this._scene.add(this._slicePoints);
      // contours.forEach(cntr => {
      //   let cntrGeom = new THREE.Geometry();
      //   cntrGeom.vertices = cntr;
      //   let contour = new THREE.Line(cntrGeom, new THREE.LineBasicMaterial({
      //     color: Math.random() * 0xffffff //0x777777 + 0x777777
      //   }));
      //   this._scene.add(contour);
      // });
    },
    render() {
      this._renderer.clear()
      this._renderer.render(this._scene, this._camera)
    },

    throttled_compute: throttle(1000 / 30, function() {
      this.currentVariables = this.computeCurrentVariables()
      this.updateColors()
      this.updateDisplacements()
      this.render()
    }),

    debounceSemgentVariables: debounce(function(val) {
      this.currentSegmentVariables = val
    }, 200),

    drawLegend() {
      if (!this.showLegend) return
      let legendComp = legend({
        color: this._colorScale.nice(),
        title: this.currentVar,
        width: Math.min(320, this.size.width * 0.8),
        ticks: 6,
      })
      // UN poco guarro no poder hacer "update"....pero no parece muy grabe de momento
      this.$refs.legendContainer.innerHTML = ''
      this.$refs.legendContainer.appendChild(legendComp)
    },
    onMouseMove(event) {
      let rect = this.$refs.container.getBoundingClientRect()
      this._mouse.x = ((event.clientX - rect.x) / this.size.width) * 2 - 1
      this._mouse.y = -((event.clientY - rect.y) / this.size.height) * 2 + 1
      if (!this.dragging) this.checkCaster()
    },
    checkCaster() {
      this._raycaster.setFromCamera(this._mouse, this._camera)
      var intersects = this._raycaster.intersectObject(this._mesh)
      if (intersects[0]) {
        // SOLO NOS IMPORTA SI HAY 1 intersect
        let hoveredSegment = this.segmentDict.AHA[intersects[0].face.a]
        this.$emit('highlightSegment', { id: hoveredSegment })
        this.$refs.container.children[0].__data__ = { id: hoveredSegment }
      } else {
        this.$emit('highlightSegment', null)
        this.$refs.container.children[0].__data__ = null
      }
    },
  },
  watch: {
    model() {
      this.loadModel()
      this.updateColors()
      this.render()
    },
    segmentVariables(val) {
      if (this.lazyRender) this.debounceSemgentVariables(val)
      else this.currentSegmentVariables = val
    },
    variables() {
      this.loadVariables()
      this.currentVariables = this.computeCurrentVariables()
      this.updateColors()
      this.updateDisplacements()
      this.render()
      this.drawLegend()
    },
    segmentDict() {
      this.updateColors()
    },
    currentTime() {
      // this.currentVariables = this.computeCurrentVariables()
      // this.updateColors()
      // this.updateDisplacements()
      // this.render()
      this.throttled_compute()
    },
    size() {
      this._renderer.setSize(this.size.width, this.size.height)
      this._camera.aspect = this.size.width / this.size.height
      this._camera.updateProjectionMatrix()
      this.render()
      this.drawLegend()
    },
    currentSlice() {
      this.updateSlice()
      this.render()
    },
    currentSegment() {
      // this.updateSlice()
      this.updateColors()
      this.render()
    },
    currentVar() {
      // this.updateSlice()
      this.loadVariables()
      this.updateColors()
      this.render()
      this.drawLegend()
    },
    dataBundle() {
      if (this.dataBundle && this.dataBundle.varKeys.length > 0) {
        this.currentVar = this.dataBundle.varKeys[0]
      }
    },
  },
}
</script>

<style scoped lang="stylus">
.model-widget
  position: relative
  overflow: visible !important
  outline: none

  .controls
    position: absolute
    right: 60px
    z-index: 2
    display: flex
    flex: 0 0 auto
    flex-wrap: wrap
    justify-content: flex-end
    align-items: flex-end
    max-width: 100%
    transform: translate(0, -47px) // GUARRO GUARRO.... :D

    .multi-select-component
      margin: 0 5px

  .three-container
    position: absolute
    width: 100%
    height: 90%

  .legend
    position: absolute
    bottom: 10px
    width: 100%

    >>> svg
      margin: auto
</style>